import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Shield, MapPin } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = ({ addToCart }) => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/categories`),
        ]);
        
        let featured = productsRes.data.slice(0, 4);
        
        // If no featured products, get some popular products with prices > 0
        if (featured.length === 0) {
          const allProducts = await axios.get(`${API}/products`);
          // Filter products with price > 0 and take first 4
          featured = allProducts.data
            .filter(p => p.price > 0)
            .slice(0, 4);
        }
        
        setFeaturedProducts(featured);
        setCategories(categoriesRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categoryIcons = {
    vaporizers: "💨",
    "e-liquids": "🧪",
    accessories: "🔧",
    hardware: "⚙️",
    "starter-kits": "📦",
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1746421867362-ae0fa8c2564a?w=1920&q=80"
            alt="Hero Background"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/60 via-transparent to-background/60" />
        </div>

        {/* Neon Effects */}
        <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-unbounded text-4xl sm:text-5xl lg:text-7xl font-bold mb-6">
              <span className="gradient-text">ELEVATE</span>
              <br />
              <span className="text-foreground">YOUR EXPERIENCE</span>
            </h1>
            <p className="font-outfit text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Premium vaporizers, e-liquids, and accessories. Curated for the
              modern enthusiast.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="hero-shop-btn"
                asChild
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-outfit font-semibold px-8 py-6 text-lg rounded-xl neon-box"
              >
                <Link to="/shop">
                  Shop Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                data-testid="hero-location-btn"
                asChild
                variant="outline"
                className="border-border text-foreground hover:bg-muted font-outfit px-8 py-6 text-lg rounded-xl"
              >
                <Link to="/about">
                  <MapPin className="mr-2 w-5 h-5" />
                  Visit Store
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-primary rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-8"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-unbounded text-lg font-semibold text-foreground mb-2">
                Premium Quality
              </h3>
              <p className="font-outfit text-muted-foreground">
                Only the finest products from trusted brands
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center p-8"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Shield className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="font-unbounded text-lg font-semibold text-foreground mb-2">
                Expert Support
              </h3>
              <p className="font-outfit text-muted-foreground">
                Knowledgeable staff ready to assist you
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center p-8"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-accent/10 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-unbounded text-lg font-semibold text-foreground mb-2">
                Easy Pickup
              </h3>
              <p className="font-outfit text-muted-foreground">
                Order online, pick up at our Gardner location
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-unbounded text-3xl md:text-4xl font-bold text-foreground mb-4">
              Shop by Category
            </h2>
            <p className="font-outfit text-muted-foreground">
              Find exactly what you're looking for
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <Link
                  to={`/shop/${category.id}`}
                  data-testid={`category-${category.id}`}
                  className="block p-6 rounded-xl bg-card border border-border hover:border-primary/50 transition-all duration-300 text-center"
                >
                  <span className="text-4xl mb-3 block">
                    {categoryIcons[category.id] || "📦"}
                  </span>
                  <span className="font-outfit text-sm font-medium text-foreground">
                    {category.name}
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-between mb-12"
          >
            <div>
              <h2 className="font-unbounded text-3xl md:text-4xl font-bold text-foreground mb-2">
                Featured Products
              </h2>
              <p className="font-outfit text-muted-foreground">
                Hand-picked favorites from our collection
              </p>
            </div>
            <Button
              data-testid="view-all-products-btn"
              asChild
              variant="outline"
              className="hidden md:flex border-border hover:bg-muted"
            >
              <Link to="/shop">
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-xl bg-muted animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  addToCart={addToCart}
                  index={index}
                />
              ))}
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <Button
              data-testid="view-all-products-mobile-btn"
              asChild
              className="bg-primary text-primary-foreground"
            >
              <Link to="/shop">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Store Info Banner */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-unbounded text-3xl md:text-4xl font-bold text-foreground mb-4">
              Visit Our Store
            </h2>
            <p className="font-outfit text-lg text-muted-foreground mb-6">
              1823 E Santa Fe St, Gardner, KS 66030
              <br />
              Open Daily: 10 AM - 8 PM
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="call-store-btn"
                asChild
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <a href="tel:+19136051001">Call (913) 605-1001</a>
              </Button>
              <Button
                data-testid="get-directions-btn"
                asChild
                variant="outline"
                className="border-border hover:bg-muted"
              >
                <a
                  href="https://maps.app.goo.gl/7jYJikWFQLMz9qhK9"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get Directions
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
